import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'

function transformProfile(row, org) {
  return {
    id: row.id,
    name: row.full_name || row.name,
    fullName: row.full_name || row.name,
    username: row.username,
    email: row.email,
    phone: row.phone || '',
    role: row.role,
    organizationId: row.organization_id,
    branchId: row.branch_id,
    isActive: row.is_active,
    mustChangePassword: row.must_change_password,
    // null → true so existing users (pre-migration) skip onboarding
    onboardingCompleted: row.onboarding_completed ?? true,
    // Subscription state is read from the organization, which owns it. The
    // copy on profiles.trial_ends_at is only a fallback for rows written
    // before applyPlanAndTrial started syncing both — keeping two sources
    // meant the lock could disagree with the org's actual state.
    trialEndsAt: org?.trial_ends_at
      ? new Date(org.trial_ends_at)
      : row.trial_ends_at ? new Date(row.trial_ends_at) : null,
    subscriptionStatus: org?.subscription_status ?? 'trialing',
    currentPeriodEnd: org?.current_period_end ? new Date(org.current_period_end) : null,
    suspendedAt: org?.suspended_at ? new Date(org.suspended_at) : null,
    suspendedReason: org?.suspended_reason || '',
    graceUntil: org?.grace_until ? new Date(org.grace_until) : null,
  }
}

export function useAuth() {
  const [currentUser, setCurrentUser] = useState(null)
  const [authUser, setAuthUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loginError, setLoginError] = useState('')
  const [signUpError, setSignUpError] = useState('')
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  // Prevents onAuthStateChange from running fetchProfile during our own signUp flow
  const signingUpRef = useRef(false)
  const recoveryRef = useRef(false)

  function enterRecovery() { recoveryRef.current = true; setIsPasswordRecovery(true) }
  function exitRecovery()  { recoveryRef.current = false; setIsPasswordRecovery(false) }

  const fetchProfile = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error || !data) {
      await supabase.auth.signOut()
      setCurrentUser(null)
      setAuthUser(null)
    } else if (!data.is_active) {
      await supabase.auth.signOut()
      setCurrentUser(null)
      setAuthUser(null)
      setLoginError('This account has been deactivated. Contact your administrator.')
    } else {
      // Fetch org subscription status alongside profile (non-blocking if missing).
      let org = null
      if (data.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('subscription_status, current_period_end, plan, trial_ends_at, suspended_at, suspended_reason, grace_until')
          .eq('id', data.organization_id)
          .single()
        org = orgData
      }
      setCurrentUser(transformProfile(data, org))
      // Stamp last-seen for HQ's user detail (fire-and-forget; RPC is a no-op
      // until last_seen_migration.sql has been run).
      supabase.rpc('touch_last_seen').then(() => {}, () => {})
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    const hash  = new URLSearchParams(window.location.hash.replace('#', '?'))
    const query = new URLSearchParams(window.location.search)
    if (hash.get('type') === 'recovery' || query.get('type') === 'recovery') {
      enterRecovery()
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (recoveryRef.current) { setLoading(false); return }
      if (session?.user) {
        setAuthUser(session.user)
        fetchProfile(session.user.id)
      } else {
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') { enterRecovery(); setLoading(false); return }
      if (recoveryRef.current) return
      if (signingUpRef.current) return

      if (session?.user) {
        setAuthUser(session.user)
        fetchProfile(session.user.id)
      } else {
        setCurrentUser(null)
        setAuthUser(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [fetchProfile])

  async function login(email, password) {
    setLoginError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      const msg = error.message === 'Invalid login credentials'
        ? 'Invalid email or password.'
        : error.message.toLowerCase().includes('not confirmed')
        ? 'Please verify your email first — check your inbox (and spam) for the confirmation link.'
        : error.message
      setLoginError(msg)
    }
  }

  async function signUp({ fullName, email, phone, password, orgName, businessType, cacNumber, numBranches, companySize, plan }) {
    setSignUpError('')
    signingUpRef.current = true

    // Provision the account server-side (auth user + organization + first
    // branch + admin profile) via the signup function. When email
    // confirmations are enabled, Supabase emails a verification link and the
    // user can't sign in until they click it.
    const { data, error } = await supabase.functions.invoke('signup', {
      body: {
        fullName, email, phone, password, orgName, businessType, cacNumber, numBranches, companySize, plan,
        redirectTo: `${window.location.origin}/login`,
      },
    })

    if (error) {
      let msg = 'Signup failed. Please try again.'
      try {
        const body = await error.context.json()
        if (body?.error) msg = body.error
      } catch { /* keep default */ }
      signingUpRef.current = false
      setSignUpError(msg)
      return { success: false }
    }

    if (data?.needsVerification) {
      signingUpRef.current = false
      return { success: true, needsVerification: true, email }
    }

    // Sign the new admin in → lands straight on the dashboard.
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
    if (signInError) {
      signingUpRef.current = false
      setSignUpError('Account created — please sign in to continue.')
      return { success: false }
    }

    const { data: { session } } = await supabase.auth.getSession()
    signingUpRef.current = false
    if (session?.user) {
      setAuthUser(session.user)
      await fetchProfile(session.user.id)
    }
    return { success: true }
  }

  // Resend the confirmation email. Works pre-session (pass the email from the
  // signup form) or for a signed-in-but-unconfirmed session.
  async function resendVerification(email) {
    let target = email
    if (!target) {
      const { data: { session } } = await supabase.auth.getSession()
      target = session?.user?.email
    }
    if (!target) return { success: false, message: 'No email to send to.' }
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: target,
      options: { emailRedirectTo: `${window.location.origin}/login` },
    })
    if (error) return { success: false, message: error.message }
    return { success: true }
  }

  async function refetchProfile() {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) {
      setAuthUser(session.user)
      await fetchProfile(session.user.id)
    }
  }

  async function logout() {
    await supabase.auth.signOut()
  }

  // Decision 9: self-service account deletion via the delete-account function.
  async function deleteAccount() {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return { success: false, message: 'No active session.' }
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
      { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` } }
    )
    if (!res.ok) {
      const json = await res.json().catch(() => ({}))
      return { success: false, message: json.error || 'Failed to delete account.' }
    }
    await supabase.auth.signOut()
    return { success: true }
  }

  async function changePassword(newPassword) {
    let userId = currentUser?.id
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession()
      userId = session?.user?.id
    }
    if (userId) await supabase.rpc('clear_must_change_password')
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
    setCurrentUser((prev) => prev ? { ...prev, mustChangePassword: false } : prev)
    exitRecovery()
  }

  async function sendPasswordReset(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    })
    if (error) return { success: false, message: error.message }
    return { success: true }
  }

  const emailVerified = !!(authUser?.email_confirmed_at)

  return {
    currentUser,
    authUser,
    loading,
    loginError,
    signUpError,
    emailVerified,
    isPasswordRecovery,
    login,
    signUp,
    logout,
    deleteAccount,
    changePassword,
    sendPasswordReset,
    resendVerification,
    refetchProfile,
  }
}
